import React, { useState, useEffect, useRef, createRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UsernameAndPasswordSettings from "./PSUsernamePasswordSettings";
import AddressComponent from "./PSAddressComponent";
import PhoneNotificationsComponent from "./PSPhoneNotificationsComponent";
import EmergencyContactComponent from "./PSEmergencyContactComponent";
import EmailPreferencesComponent from "./PSEmailPreferencesComponent";

const AccountSettingsComponent = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const tabsRefs = useRef({});
  const tabsGroupRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const tabNames = {
    credentials: "Login Credentials",
    address: "Address",
    "phone-notifications": "Phone Notifications",
    "emergency-contacts": "Emergency Contacts",
    "email-alerts": "Email Alerts",
  };

  const tabComponents = {
    credentials: <UsernameAndPasswordSettings />,
    address: <AddressComponent />,
    "phone-notifications": <PhoneNotificationsComponent />,
    "emergency-contacts": <EmergencyContactComponent />,
    "email-alerts": <EmailPreferencesComponent />,
  };

  Object.keys(tabNames).forEach((key) => {
    if (!tabsRefs.current[key]) {
      tabsRefs.current[key] = createRef();
    }
  });

  const [activeTab, setActiveTab] = useState(tab || "credentials");

  useEffect(() => {
    console.log("Active tab set to:", tab || "credentials");
    setActiveTab(tab || "credentials");
  }, [tab]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    console.log(`Scrolling logic for active tab: ${activeTab}`);
    if (activeTab && tabsRefs.current[activeTab]?.current) {
      const tabElement = tabsRefs.current[activeTab].current;
      if (tabElement) {
        const tabsContainer = tabElement.parentNode;
        const containerWidth = tabsContainer.offsetWidth;
        const tabWidth = tabElement.offsetWidth;
        const tabLeft = tabElement.offsetLeft;
        const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

        console.log(
          `Scrolling to position: ${scrollLeft} (container width: ${containerWidth}, tab width: ${tabWidth}, tab left: ${tabLeft})`
        );

        const endOfTab = tabLeft + tabWidth;
        const endOfContainer = tabsContainer.scrollWidth;

        if (endOfTab > endOfContainer - containerWidth / 2) {
          tabsContainer.scrollTo({
            left: endOfContainer - containerWidth,
            behavior: "smooth",
          });
          console.log("Scrolling to the right edge of container.");
        } else if (tabLeft < containerWidth / 2) {
          tabsContainer.scrollTo({
            left: 0,
            behavior: "smooth",
          });
          console.log("Scrolling to the left edge of container.");
        } else {
          tabsContainer.scrollTo({
            left: scrollLeft,
            behavior: "smooth",
          });
        }
      }
    }
  }, [activeTab, windowWidth]);

  const handleTabClick = (key) => {
    console.log(`Tab clicked: ${key}`);
    navigate(`/account/ps-dashboard/account-settings/${key}`);
    setActiveTab(key);

    const lastTabKey = Object.keys(tabNames).pop();
    if (key === lastTabKey) {
      console.log(`Last tab clicked: ${tabNames[key]}`);
      scrollToFarRight();
    }
  };

  const scrollToFarRight = () => {
    if (tabsGroupRef.current) {
      const tabsGroupElement = tabsGroupRef.current;
      tabsGroupElement.scrollTo({
        left: tabsGroupElement.scrollWidth,
        behavior: "smooth",
      });
      console.log("Scrolling the tabs group to the far right.");
    }
  };

  return (
    <div className="post-jobs-container">
      <div className="post-jobs-tabs" ref={tabsRefs}>
        <div className="tabs-group" ref={tabsGroupRef}>
          <div className="tabs">
            {Object.entries(tabComponents).map(([key, component]) => (
              <div
                key={key}
                ref={tabsRefs.current[key]}
                className={`tab ${activeTab === key ? "active" : ""}`}
                onClick={() => handleTabClick(key)}
              >
                {tabNames[key]}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="post-job-tab-content">{tabComponents[activeTab]}</div>
    </div>
  );
};

export default AccountSettingsComponent;
